/**
 * Bandwith Design Tokens
 *
 * Shared across web (Tailwind) and native (React Strict DOM).
 * Values match packages/styles/theme.css (checked by theme-parity.test.ts):
 * `colors` is the dark palette, `lightColors` the light one. CSS-only
 * colors such as amber-glow have no entry here.
 */
export const tokens = {
  colors: {
    // Backgrounds
    bgPrimary: "#0D0C0B",
    bgSecondary: "#161514",
    bgTertiary: "#1F1D1B",

    // Borders
    borderSubtle: "#2A2725",
    borderAccent: "#3D3632",

    // Text
    textPrimary: "#F5F2ED",
    textSecondary: "#B8B2A8",
    textMuted: "#7A756D",
    textDisabled: "#4A4640",

    // Accents
    amber: "#E8A848",
    amberWarm: "#D4943E",
    copper: "#C87C4A",
    redMuted: "#C45C4D",
    greenMuted: "#6B9B7A",

    // Text on an amber or red fill; strings and frets in chord diagrams
    onAccent: "#0D0C0B",
    gridLine: "#6B6560",

    // Semantic colors
    white: "#FFFFFF",
    red600: "#dc2626",
  },

  lightColors: {
    bgPrimary: "#F6F1E8",
    bgSecondary: "#EEE7DB",
    bgTertiary: "#E5DCCC",

    borderSubtle: "#DCD2C2",
    borderAccent: "#C4B6A2",

    textPrimary: "#1A1714",
    textSecondary: "#4B453D",
    textMuted: "#6E665B",
    textDisabled: "#A39A8C",

    amber: "#9A5C0C",
    amberWarm: "#7F4B08",
    copper: "#975428",
    redMuted: "#A33B2B",
    greenMuted: "#3A6E4C",

    onAccent: "#FFFDF8",
    gridLine: "#8A8176",
  },

  fonts: {
    display: "Instrument Serif",
    body: "Concourse",
    mono: "Courier Prime",
  },

  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
  },

  radii: {
    sm: 4,
    md: 6,
    lg: 8,
    full: 9999,
  },
} as const;

/**
 * Theme-aware colors as CSS variable references, for styles written in
 * TypeScript (CodeMirror themes, inline styles). They follow the active
 * theme without a re-render. Use `themeAlpha` for translucent variants.
 */
export const themeColors = {
  bgPrimary: "var(--bw-bg-primary)",
  bgSecondary: "var(--bw-bg-secondary)",
  bgTertiary: "var(--bw-bg-tertiary)",
  borderSubtle: "var(--bw-border-subtle)",
  borderAccent: "var(--bw-border-accent)",
  textPrimary: "var(--bw-text-primary)",
  textSecondary: "var(--bw-text-secondary)",
  textMuted: "var(--bw-text-muted)",
  textDisabled: "var(--bw-text-disabled)",
  amber: "var(--bw-amber)",
  copper: "var(--bw-copper)",
  greenMuted: "var(--bw-green-muted)",
} as const;

/** A theme color at the given opacity (0–1), e.g. a selection highlight. */
export function themeAlpha(
  color: keyof typeof themeColors,
  opacity: number,
): string {
  return `color-mix(in srgb, ${themeColors[color]} ${Math.round(opacity * 100)}%, transparent)`;
}
